import { useEffect, useMemo, useState } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { requireSupabase } from "@/lib/supabase";
import { SectionCard } from "@/components/common/SectionCard";
import type { OrgPickRow } from "./devPanelTypes";
import { isUuid } from "./devPanelShared";
import { DevInboundReceiptsFilters } from "./DevInboundReceiptsFilters";
import { DevInboundReceiptsRow } from "./DevInboundReceiptsRow";
import { useInboundReceipts } from "./useInboundReceipts";

const PLATFORM_ROLES = ["platform_owner", "platform_admin", "platform_dev"] as const;

export function DevInboundReceiptsPanel() {
  const auth = useAuthOptional();
  const canAccess = auth?.status === "ready" && auth.realHasRole(...PLATFORM_ROLES);

  const sb = useMemo(() => {
    try {
      return requireSupabase();
    } catch {
      return null;
    }
  }, []);

  const [orgs, setOrgs] = useState<OrgPickRow[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState("");

  useEffect(() => {
    if (!canAccess || !auth?.session || !sb) {
      setOrgs([]);
      setOrgsError(null);
      return;
    }
    let cancelled = false;
    setOrgsLoading(true);
    setOrgsError(null);
    void (async () => {
      try {
        const { data, error } = await sb.rpc("platform_list_orgs");
        if (cancelled) return;
        if (error) {
          setOrgsError(error.message || String(error));
          setOrgs([]);
          return;
        }
        const rows = ((data as { id?: string; name?: string }[]) || [])
          .map((r) => ({
            id: String(r.id || ""),
            name: String(r.name || r.id || "").trim() || String(r.id),
          }))
          .filter((r) => r.id && isUuid(r.id))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setOrgs(rows);
        setOrganizationId((prev) => prev || rows[0]?.id || "");
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, auth?.session, sb]);

  const receipts = useInboundReceipts({
    enabled: canAccess && Boolean(organizationId),
    organizationId,
  });

  if (!canAccess) return null;

  return (
    <SectionCard
      defaultCollapsed
      title="Inbound receipts"
      description="Original JSON stored on ops_integration_events (IdeaRoom, inventory host, named webhooks, FastField, QBO). Platform_dev / platform_owner."
    >
      <DevInboundReceiptsFilters
        organizationId={organizationId}
        onOrganizationId={setOrganizationId}
        orgs={orgs}
        orgsLoading={orgsLoading}
        sources={receipts.sources}
        filters={receipts.filters}
        patchFilters={receipts.patchFilters}
        working={receipts.loading}
        onRefresh={() => void receipts.refresh()}
        onReset={receipts.resetFilters}
      />
      {orgsError ? (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{orgsError}</div>
      ) : null}
      {receipts.error ? (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{receipts.error}</div>
      ) : null}
      <ul className="mt-3 space-y-2">
        {receipts.events.map((row) => (
          <DevInboundReceiptsRow
            key={row.id}
            row={row}
            expanded={receipts.expandedId === row.id}
            payloadBusy={receipts.payloadBusyId === row.id}
            detail={receipts.payloadById[row.id]}
            onToggle={() => void receipts.expandRow(row.id)}
          />
        ))}
        {receipts.events.length === 0 && !receipts.loading ? (
          <li className="text-sm text-gray-500">
            {organizationId ? "No receipts match these filters." : "Select an organization."}
          </li>
        ) : null}
      </ul>
    </SectionCard>
  );
}
